import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPageBySlug, getCollection, hasFixedDate } from "../lib/cms";
import { 
  Check, 
  AlertCircle, 
  HelpCircle, 
  Info, 
  Calendar, 
  ChevronDown, 
  Calculator, 
  DollarSign, 
  User, 
  Clock, 
  Car, 
  ShieldCheck,
  ArrowRight,
  Sparkles
} from "lucide-react";

export default function Priser() {
  const [pageData, setPageData] = useState(null);
  const [prices, setPrices] = useState([]);
  const [holdstarts, setHoldstarts] = useState([]);
  const [loading, setLoading] = useState(true);

  // FAQ Accordion State
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await getPageBySlug("priser");
        const priceItems = await getCollection("prices");
        const holdstartItems = await getCollection("holdstart_dates");
        
        setPageData(page);
        setPrices(priceItems);
        
        // Sort holdstarts so active/available ones are shown nicely
        if (holdstartItems) {
          setHoldstarts(holdstartItems);
        }
      } catch (err) {
        console.error("Error loading prices page data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "");
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brand-primary"></div>
          <div className="text-brand-primary font-bold text-lg">Henter priser og holdstarts...</div>
        </div>
      </div>
    );
  }

  // Find specific packages
  const lovpakken = prices.find(p => p.title === "Lovpakken") || {
    price: "14.500 kr.",
    title: "Lovpakken",
    subtitle: "Alt det lovpligtige undervisning",
    included: "<ul><li>30 teorilektioner á 45 min.</li><li>3 kørelektioner på manøvre-kravlegård inkl. leje af bane med bil.</li><li>4 kørelektioner på køreteknisk-glatbane inkl. leje af bane med bil.</li><li>17 kørelektioner på vej á 45 min.</li><li>Kørekortet kan også tages med automatgear (kode 78/148).</li></ul>",
    extra_costs: ""
  };

  const otherPackages = prices.filter(p => p.title !== "Lovpakken");

  // FAQ Items
  const faqs = [
    {
      q: "Kan jeg betale Lovpakken i rater?",
      a: "Ja, selvfølgelig! Hos Carinas Køreskole kan du dele betalingen af Lovpakken op i 2 rater (8.000 kr. for 1. rate ved holdstart, og 6.500 kr. for 2. rate efter cirka 4-5 uger). Der er ingen ekstra gebyrer eller renter forbundet med denne opdeling."
    },
    {
      q: "Koster det ekstra at tage kørekort med automatgear?",
      a: "Nej, overhovedet ikke. Hos mig koster det præcis det samme, uanset om du vælger manuelt gear i vores Audi Q3 eller automatgear i vores moderne, lydløse elbil VW ID.3. Du vælger blot det, der passer bedst til dig."
    },
    {
      q: "Hvad sker der, hvis jeg dumper en teoriprøve eller køreprøve?",
      a: "Hvis du dumper en prøve, skal der betales et nyt prøvegebyr til Færdselsstyrelsen på 1.600 kr. Derudover er der et administrationsgebyr på 250 kr., samt leje af skolevognen til den nye køreprøve (1.000 kr.) og eventuelle ekstra kørelektioner (695 kr. pr. stk.), som vi aftaler for at sikre, at du er helt klar næste gang."
    },
    {
      q: "Hvornår skal jeg melde afbud til en køretime?",
      a: "Afbud til en køretime skal ske senest kl. 7.00 på selve dagen for din køretime. Det skal ske via SMS eller opkald til mig på tlf. 30 51 72 69. Ved for sent afbud, sygdom efter kl. 7.00 eller manglende lektionsplan opkræves fuld pris (695 kr.) for timen, da jeg ikke kan nå at give den til en anden elev."
    },
    {
      q: "Hvor lang tid er mit førstehjælpsbevis gyldigt?",
      a: "Et færdselsrelateret førstehjælpskursus (8 timer) er gyldigt i 1 år fra udstedelsesdatoen. Hvis du allerede har et gyldigt bevis (under 1 år gammelt), behøver du ikke tage et nyt – du skal blot medbringe det originale bevis til mig."
    }
  ];

  return (
    <div className="bg-stone-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-white border-b border-stone-200 overflow-hidden py-16 lg:py-24">
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-40"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Hero Text */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" /> 100% Gennemskuelige Priser
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-brand-dark tracking-tight leading-tight">
                Priser på kørekort <br />
                <span className="text-brand-primary">— helt uden overraskelser</span>
              </h1>
              <p className="text-stone-600 text-lg sm:text-xl leading-relaxed max-w-2xl">
                Hos Carinas Køreskole i Gug får du personlig, tålmodig og professionel undervisning. Her lister vi alle udgifter ærligt op, så du præcis ved, hvad det koster at få kørekortet i hus.
              </p>
              
              {/* Quick Trust Badges */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="flex items-center gap-3 bg-stone-50 p-3.5 rounded-2xl border border-stone-200/60">
                  <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary shrink-0">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 font-medium">Betaling</p>
                    <p className="text-sm font-bold text-brand-dark">Opdel i 2 rater</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-stone-50 p-3.5 rounded-2xl border border-stone-200/60">
                  <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary shrink-0">
                    <Car className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 font-medium">Automatgear</p>
                    <p className="text-sm font-bold text-brand-dark">Ingen merpris</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-stone-50 p-3.5 rounded-2xl border border-stone-200/60">
                  <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary shrink-0">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 font-medium">Gennemsigtighed</p>
                    <p className="text-sm font-bold text-brand-dark">Ingen skjulte gebyrer</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Carina Quote & Portrait */}
            <div className="lg:col-span-5">
              <div className="relative">
                {/* Decorative background shape */}
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-brand-primary/20 to-amber-500/10 blur-lg -z-10"></div>
                
                <div className="relative rounded-3xl border border-stone-200 overflow-hidden shadow-xl aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] z-10">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/mig.jpg" 
                    alt="Carina Svensson - Din Kørelærer" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent p-6 text-white">
                    <p className="text-lg font-bold">Carina Svensson</p>
                    <p className="text-xs text-stone-200 font-medium">Din kørelærer i Gug, Aalborg SØ</p>
                  </div>
                </div>

                {/* Floating Car 1: Audi Q3 */}
                <div className="absolute -top-6 -left-6 sm:-left-10 z-20 w-28 sm:w-36 bg-white p-1.5 rounded-2xl shadow-xl border border-stone-100 animate-float-slow">
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-stone-100">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=80,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/9a7044-321b9f7c775a4e86a5b9f48c2a0bac79-mv2.jpg" 
                      alt="Audi Q3 Skolevogn" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-1.5 px-1 text-center">
                    <p className="text-[10px] sm:text-xs font-bold text-brand-dark">Audi Q3</p>
                    <p className="text-[8px] sm:text-[10px] text-stone-500 font-medium">Manuelt gear</p>
                  </div>
                </div>

                {/* Floating Car 2: VW ID.3 */}
                <div className="absolute -bottom-6 -right-6 sm:-right-10 z-20 w-28 sm:w-36 bg-white p-1.5 rounded-2xl shadow-xl border border-stone-100 animate-float-delayed">
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-stone-100">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=80,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/id-3-2.jpeg" 
                      alt="VW ID.3 Skolevogn" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-1.5 px-1 text-center">
                    <p className="text-[10px] sm:text-xs font-bold text-brand-dark">VW ID.3 Elbil</p>
                    <p className="text-[8px] sm:text-[10px] text-stone-500 font-medium">Automatgear</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Lovpakken Spotlight */}
      <section className="py-16 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-brand-primary font-bold text-sm uppercase tracking-wider block mb-2">Vores mest populære forløb</span>
          <h2 className="text-3xl sm:text-4xl font-black text-brand-dark tracking-tight">Lovpakken til Personbil</h2>
          <p className="text-stone-600 mt-3 text-base sm:text-lg">
            Alt det lovpligtige undervisning, du skal bruge for at få dit kørekort. Vi kører i enten en Audi Q3 (manuelt gear) eller en VW ID.3 Elbil (automatgear).
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-stone-200 shadow-xl overflow-hidden grid grid-cols-1 lg:grid-cols-12 items-stretch">
          {/* Lovpakken Left - What is included */}
          <div className="lg:col-span-7 p-6 sm:p-10 lg:p-12 flex flex-col justify-between">
            <div className="space-y-8">
              <div>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider mb-3">
                  Mest populær
                </span>
                <h3 className="text-2xl sm:text-3xl font-black text-brand-dark">{lovpakken.title}</h3>
                <p className="text-stone-500 text-sm font-medium mt-1">{lovpakken.subtitle}</p>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-stone-400 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-primary"></span> Hvad er inkluderet i prisen:
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 bg-stone-50 p-4 rounded-2xl border border-stone-200/40">
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-bold text-brand-dark text-sm">30 Teoritimer</p>
                      <p className="text-xs text-stone-500">Ubegrænset teori i Gug</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-stone-50 p-4 rounded-2xl border border-stone-200/40">
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-bold text-brand-dark text-sm">Manøvrebane</p>
                      <p className="text-xs text-stone-500">3 lektioner inkl. baneleje</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-stone-50 p-4 rounded-2xl border border-stone-200/40">
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-bold text-brand-dark text-sm">Glatbane</p>
                      <p className="text-xs text-stone-500">4 lektioner på køreteknisk anlæg</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-stone-50 p-4 rounded-2xl border border-stone-200/40">
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-bold text-brand-dark text-sm">17 Køretimer på vej</p>
                      <p className="text-xs text-stone-500">Rolig og tålmodig kørsel</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-amber-50/60 border border-amber-200/60 rounded-2xl flex items-start gap-3">
                  <Info className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
                  <p className="text-xs text-stone-600 leading-relaxed">
                    <strong>Automatgear uden merpris:</strong> Du kan vælge at tage kørekortet i vores VW ID.3 elbil. Det giver dig kode 78/148, hvilket i dag er utrolig populært og gør kørslen meget lettere!
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8 mt-8 border-t border-stone-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs text-stone-500 font-medium">Betalingsbetingelser</p>
                <p className="text-sm font-bold text-brand-dark">Lovpakken betales i 2 rater (8.000 kr. ved 1. rate & 6.500 kr. ved 2. rate)</p>
              </div>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 bg-brand-primary text-white font-bold text-sm px-6 py-3.5 rounded-xl hover:bg-brand-primary-hover shadow-md transition-all duration-200"
              >
                Tilmeld dig holdstart <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Lovpakken Right - Price & Extra Costs */}
          <div className="lg:col-span-5 bg-stone-50 border-t lg:border-t-0 lg:border-l border-stone-200 p-6 sm:p-10 lg:p-12 flex flex-col justify-between">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center shadow-sm">
                <p className="text-xs text-stone-500 font-bold uppercase tracking-wider mb-1">Pris for Lovpakken</p>
                <span className="text-4xl sm:text-5xl font-black text-brand-primary block">{lovpakken.price}</span>
                <span className="text-xs text-stone-500 font-medium mt-1.5 block">Inkl. moms og alle lovpligtige timer</span>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-stone-400 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 text-brand-primary" />
                  Forventede yderligere udgifter:
                </h4>
                <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3 shadow-sm">
                  <div className="flex justify-between items-center text-sm border-b border-stone-100 pb-2">
                    <span className="text-stone-600">Prøvegebyr (Færdselsstyrelsen)</span>
                    <span className="font-bold text-brand-dark">1.600 kr.</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-stone-100 pb-2">
                    <span className="text-stone-600">Leje af skolevogn og kørelærer til køreprøve</span>
                    <span className="font-bold text-brand-dark">1.000 kr.</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-stone-100 pb-2">
                    <span className="text-stone-600">Online teori (Drive4you, 3 mdr.)</span>
                    <span className="font-bold text-brand-dark">300 kr.</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-stone-100 pb-2">
                    <span className="text-stone-600">Førstehjælpskursus (ca.)</span>
                    <span className="font-bold text-brand-dark">700 kr.</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-stone-100 pb-2">
                    <span className="text-stone-600">Lægeerklæring (ca.)</span>
                    <span className="font-bold text-brand-dark">450 kr.</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-stone-600">Pasfoto (ca.)</span>
                    <span className="font-bold text-brand-dark">125 kr.</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-xs text-stone-500 leading-relaxed bg-white p-4 rounded-2xl border border-stone-200/60">
              * Ekstra køretimer udover de 16 lovpligtige koster <strong>695 kr.</strong> pr. lektion á 45 minutter. De fleste elever har brug for et par ekstra timer inden prøven for at føle sig helt sikre.
            </div>
          </div>
        </div>
      </section>

      {/* Other Packages Section */}
      <section className="py-16 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-brand-primary font-bold text-sm uppercase tracking-wider block mb-2">Andre køreuddannelser</span>
          <h2 className="text-3xl sm:text-4xl font-black text-brand-dark tracking-tight">Vi tilbyder også...</h2>
          <p className="text-stone-600 mt-3 text-base sm:text-lg">
            Uanset om du skal generhverve dit kørekort, har brug for særlig køreundervisning, vil have trailerkort eller blot ønsker et par rutinetimer.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
          {otherPackages.map((pkg) => (
            <div
              key={pkg.id || pkg.title}
              className="bg-white border border-stone-200 rounded-3xl p-6 sm:p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-200"
            >
              <div className="space-y-6">
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div>
                    <h3 className="text-xl font-bold text-brand-dark">{pkg.title}</h3>
                    <p className="text-stone-500 text-xs font-medium mt-0.5">{pkg.subtitle}</p>
                  </div>
                  <span className="text-2xl sm:text-3xl font-black text-brand-primary whitespace-nowrap">{pkg.price}</span>
                </div>

                <div className="border-t border-stone-100 pt-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-3">Hvad er inkluderet:</h4>
                  <div
                    className="prose-sm text-stone-600 space-y-2"
                    dangerouslySetInnerHTML={{ __html: pkg.included }}
                  />
                </div>

                {pkg.extra_costs && (
                  <div className="border-t border-stone-100 pt-4 bg-stone-50/50 p-4 rounded-2xl border border-stone-200/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-400 flex items-center gap-1.5 mb-2">
                      <AlertCircle className="h-3.5 w-3.5 text-brand-primary" />
                      Yderligere udgifter:
                    </h4>
                    <div
                      className="prose-sm text-stone-500 text-xs leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: pkg.extra_costs }}
                    />
                  </div>
                )}
              </div>

              <div className="pt-6 mt-6 border-t border-stone-100">
                <Link
                  to="/kontakt"
                  className="block w-full text-center py-3 px-4 bg-stone-50 text-brand-dark border border-stone-200 rounded-xl text-sm font-bold hover:bg-stone-100 transition-all duration-200"
                >
                  Tilmeld dig eller forhør dig her
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Upcoming Holdstarts directly on the Prices page */}
      <section className="py-16 lg:py-24 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Holdstart Text */}
            <div className="lg:col-span-5 space-y-6">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider">
                <Calendar className="h-3.5 w-3.5" /> Holdstart i Gug
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-brand-dark tracking-tight leading-tight">
                Klar til at starte? <br />
                <span className="text-brand-primary">Se de næste hold her</span>
              </h2>
              <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
                Vi har løbende holdstart i vores hyggelige teorilokaler i Gug. Da vi maksimalt har <strong>8 elever pr. hold</strong> for at sikre den bedste, mest personlige undervisning, bliver pladserne hurtigt fyldt op.
              </p>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
                <Info className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
                <p className="text-xs text-stone-600 leading-relaxed">
                  <strong>Venne-rabat:</strong> Tilmeld dig sammen med en ven eller veninde, og I får begge en ekstra <strong>GRATIS kørelektion</strong> hver (Værdi 695 kr.)!
                </p>
              </div>
            </div>

            {/* Holdstart List */}
            <div className="lg:col-span-7">
              <div className="bg-stone-50 rounded-3xl border border-stone-200 p-6 sm:p-8 shadow-sm space-y-4">
                <h3 className="font-bold text-brand-dark text-lg border-b border-stone-200 pb-3">Kommende holdstarts-datoer</h3>
                
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {holdstarts.length > 0 ? (
                    holdstarts.map((hold) => {
                      const isFull = Boolean(
                        hold.is_fully_booked ||
                        (hold.status && (
                          hold.status.toLowerCase().includes("fuld") ||
                          hold.status.toLowerCase().includes("optaget") ||
                          hold.status.toLowerCase().includes("udsolgt")
                        ))
                      );

                      return (
                      <div 
                        key={hold.id} 
                        className="bg-white p-4 rounded-2xl border border-stone-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-brand-primary/40 transition-all"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-brand-primary bg-brand-primary/5 px-2 py-0.5 rounded">
                              {hold.category || "Almindelig"}
                            </span>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                              isFull 
                                ? "bg-red-100 text-red-700" 
                                : "bg-green-100 text-green-700"
                            }`}>
                              {isFull ? "FULD BOOKET" : (hold.status || "Ledig")}
                            </span>
                          </div>
                          <h4 className="font-black text-brand-dark text-base">{hold.title}</h4>
                          <p className="text-xs text-stone-500 flex items-center gap-1">
                            <Clock className="h-3 w-3 shrink-0" />{" "}
                            {hold.time && hold.time !== "Tidspunkter ikke fastsat"
                              ? hold.time
                              : hasFixedDate(hold.title)
                              ? "Oplyses ved tilmelding"
                              : "Tidspunkter ikke fastsat"}
                          </p>
                        </div>
                        
                        <Link
                          to="/kontakt"
                          className={`px-4 py-2.5 rounded-xl text-xs font-bold text-center transition-all ${
                            isFull
                              ? "bg-stone-100 text-stone-400 cursor-not-allowed border border-stone-200"
                              : "bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm"
                          }`}
                          onClick={(e) => isFull && e.preventDefault()}
                        >
                          {isFull ? "Udsolgt" : "Sikr dig en plads"}
                        </Link>
                      </div>
                    );})
                  ) : (
                    <p className="text-stone-500 text-sm text-center py-6">Ingen planlagte holdstarts i øjeblikket. Kontakt Carina for info.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Terms & Info (Afbudsregler) */}
      <section className="py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 space-y-4">
            <h3 className="text-xl font-bold text-brand-dark flex items-center gap-2">
              <Info className="h-5.5 w-5.5 text-brand-primary shrink-0" />
              Vigtig information vedr. afbud og sygdom
            </h3>
            <div className="text-stone-600 text-sm space-y-3 leading-relaxed">
              <p>
                Husk at melde afbud på <strong className="text-brand-dark">30 51 72 69</strong>, af kørelektion på dagen <strong className="text-brand-dark">inden kl. 7.00</strong>, hvis du bliver syg eller forhindret, ellers koster det 695 kr. pr. lektion - også hvis du ved fremmøde mangler lektionsplan eller glemmer at møde op.
              </p>
              <p>
                Al undervisning foregår på dansk. Alle priser er inkl. moms. Der tages forbehold for prisændringer.
              </p>
            </div>
          </div>
          <div className="lg:col-span-4 flex justify-center lg:justify-end">
            <Link
              to="/kontakt"
              className="px-6 py-3.5 bg-brand-primary text-white text-sm font-bold rounded-xl shadow hover:bg-brand-primary-hover transition-all duration-200 text-center whitespace-nowrap"
            >
              Gå til holdstart & tilmelding
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive FAQ Section */}
      <section className="pb-16 lg:pb-24 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-brand-primary font-bold text-sm uppercase tracking-wider block mb-2">Ofte stillede spørgsmål</span>
          <h2 className="text-3xl font-black text-brand-dark tracking-tight">Spørgsmål til priser & betaling</h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div 
              key={idx}
              className="bg-white rounded-2xl border border-stone-200 overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                className="w-full text-left p-5 sm:p-6 flex justify-between items-center gap-4 font-bold text-brand-dark hover:bg-stone-50/50 transition-all"
              >
                <span className="text-sm sm:text-base">{faq.q}</span>
                <ChevronDown className={`h-5 w-5 text-stone-400 shrink-0 transition-transform duration-200 ${
                  openFaq === idx ? "rotate-180 text-brand-primary" : ""
                }`} />
              </button>
              
              {openFaq === idx && (
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-stone-600 text-sm leading-relaxed border-t border-stone-100 pt-4 bg-stone-50/30">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
